import type { ChangeEvent, ComponentProps } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  alignItems,
  border,
  borderRadius,
  display,
  fontSize,
  height,
  justifyContent,
  lineHeight,
  space,
  textAlign,
  themeGet,
  width,
} from 'styled-system';

import Input from '../Input';
import { BodyRegularBR1 } from '../Typography';

const WIDTH_MULTIPLIER = 1.1; // needed to resolve ch unit issues on IE11

export interface ValueBoxProps {
  ariaLabel: string;
  isHighlighted: boolean;
  currentValue: string;
  onChange: (newValue: string) => void;
  unit: string;
}

const ValueInputWrapper = styled(BodyRegularBR1)<any>`
  ${width};
  ${height};
  ${display};
  ${justifyContent};
  ${alignItems};
  ${fontSize};
  ${lineHeight};
  ${borderRadius};
  ${border};
  ${space};
  border-color: ${({ isHighlighted }) =>
    themeGet(
      isHighlighted
        ? 'colors.sys.primary.background.default'
        : 'colors.sys.neutral.background.active'
    )};
  color: ${({ isHighlighted }) =>
    themeGet(
      isHighlighted ? 'colors.sys.primary.background.default' : 'colors.sys.neutral.border.strong'
    )};
`;

const valueInputWrapperStyles: ComponentProps<typeof ValueInputWrapper> = {
  width: '100%',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid',
  borderRadius: 'small',
  pr: 'sp_2',
};

const ValueInput = styled(Input)<any>`
  ${textAlign};
`;

const valueInputStyles: ComponentProps<typeof ValueInput> = {
  border: 'none',
  my: 0,
  py: 0,
  px: 0,
  color: 'inherit',
  textAlign: 'right',
};

const Unit = styled.span<any>`
  ${width};
  ${space};
`;

const ValueBox = ({ ariaLabel, isHighlighted, currentValue, onChange, unit }: ValueBoxProps) => {
  const [value, setValue] = useState<string>(currentValue);
  const inputField = useRef<HTMLInputElement>(undefined);

  const onWrapperClick = useCallback(() => {
    inputField.current.focus();
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      if (!Number.isInteger(+inputValue)) {
        return;
      }
      setValue(inputValue);
      onChange(inputValue);
    },
    [onChange]
  );

  const handleInputBlur = useCallback(() => {
    setValue(currentValue);
  }, [currentValue]);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue, isHighlighted]);

  return (
    <ValueInputWrapper
      {...valueInputWrapperStyles}
      isHighlighted={isHighlighted}
      onClick={onWrapperClick}
      data-testid={`${ariaLabel}-wrapper`}
    >
      <ValueInput
        {...valueInputStyles}
        ref={inputField}
        type="text"
        aria-label={ariaLabel}
        pattern="[0-9]*"
        maxLength="5"
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        width={`${value.length < 3 ? 3 : value.length * WIDTH_MULTIPLIER}ch`}
      />
      <Unit pl="sp_4">{unit}</Unit>
    </ValueInputWrapper>
  );
};

export default ValueBox;
